import { useEffect, useState } from "react";
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { 
  Box, List, ListItem, ListItemText, Typography, useTheme, 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Button, FormControl, InputLabel, Select, 
  MenuItem, Chip, OutlinedInput, Alert, Snackbar, 
  IconButton, Menu, Avatar, Stack, Paper
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EventIcon from '@mui/icons-material/Event';
import HeaderPage from "../../../components/Header";
import { tokens } from "../../../theme";
import FullCalendar from '@fullcalendar/react';
import { useAuth } from "../../../AuthContext";

const CalendarPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData, user, userData } = useAuth();
  const [currentEvents, setCurrentEvents] = useState([]);
  const [events, setEvents] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [contextMenuEvent, setContextMenuEvent] = useState(null);
  const [viewEventDialog, setViewEventDialog] = useState(false);
  const [viewingEvent, setViewingEvent] = useState(null);

  // Check if current user is admin
  const isAdmin = userData?.role !== '' && userData?.role === 'supperAdmin' || userData?.role === 'Admin';

  // Get current user's full name
  const getCurrentUserName = () => {
    if (userData?.FirstName && userData?.LastName) {
      return `${userData.FirstName} ${userData.LastName}`;
    }
    return user?.email || 'Current User';
  };

  // Get current user's ID
  const getCurrentUserId = () => {
    return userData?.Id || userData?.id || user?.uid;
  };

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'timed',
    display_Type: isAdmin ? 'public' : 'private',
    startDate: '',
    endDate: '',
    assigned_to: [], // Array of staff objects with staff_id and staff_name
    order_number: 0,
  });

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch calendar events
        const response = await getData('proccess.php', 'calender_V2');
        if (response.success) {
          // Parse assigned_to for each event
          const parsedEvents = (Array.isArray(response.data) ? response.data : []).map(event => ({
            ...event,
            assigned_to: parseAssignedTo(event.assigned_to)
          }));
          
          // Filter events based on user permissions
          const filteredEvents = filterEventsByUser(parsedEvents);
          setEvents(filteredEvents);
        }

        // Fetch staffs
        const staffResponse = await getData('proccess.php', 'staffs');
        if (staffResponse.success) {
          const staffList = Array.isArray(staffResponse.data) ? staffResponse.data : [];
          setStaffs(staffList);
        }
      } catch (err) {
        console.log(err);
        showSnackbar('Error fetching data', 'error');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData, userData]);

  // Parse assigned_to field which could be string or array
  const parseAssignedTo = (assignedTo) => {
    if (!assignedTo) return [];
    
    if (typeof assignedTo === 'string') {
      try {
        return JSON.parse(assignedTo);
      } catch (e) {
        return [];
      }
    }
    
    return Array.isArray(assignedTo) ? assignedTo : [];
  };

  // Filter events based on user role and ownership
  const filterEventsByUser = (eventsData) => {
    if (!Array.isArray(eventsData)) return [];
    
    return eventsData.filter(event => {
      // Admin sees all events
      if (isAdmin) return true;
      
      // For non-admin staff
      const assignedTo = event.assigned_to || [];
      const currentUserId = getCurrentUserId();
      
      // Check if current user is assigned to this event
      const isAssigned = assignedTo.some(staff => 
        String(staff.staff_id) === String(currentUserId)
      );
      
      // Show public events OR events where user is assigned
      return event.display_Type === 'public' || isAssigned;
    });
  };

  const handleDateClick = (selected) => {
    // Pre-fill form with selected date
    const currentUserId = getCurrentUserId();
    const currentUserName = getCurrentUserName();
    
    setFormData({
      title: '',
      description: '',
      event_type: selected.allDay ? 'alltime' : 'timed',
      display_Type: isAdmin ? 'public' : 'private',
      startDate: selected.dateStr,
      endDate: selected.dateStr,
      assigned_to: isAdmin ? [] : [{
        staff_id: currentUserId,
        staff_name: currentUserName
      }], // Auto-assign to current user if not admin
      order_number: 0,
    });
    setSelectedEvent(null);
    setOpenDialog(true);
  };

  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    const extendedProps = event.extendedProps;
    
    // For viewing details (non-editable for non-owners)
    setViewingEvent({
      id: event.id,
      title: event.title,
      description: extendedProps.description || '',
      event_type: extendedProps.event_type,
      display_Type: extendedProps.display_Type,
      start: event.start,
      end: event.end,
      assigned_to: extendedProps.assigned_to || [],
      order_number: extendedProps.order_number,
      created_by: extendedProps.created_by
    });
    setViewEventDialog(true);
  };

  const handleEditEvent = () => {
    setViewEventDialog(false);
    
    if (viewingEvent) {
      setSelectedEvent(viewingEvent);
      setFormData({
        title: viewingEvent.title,
        description: viewingEvent.description || '',
        event_type: viewingEvent.event_type || 'timed',
        display_Type: viewingEvent.display_Type || 'private',
        startDate: viewingEvent.start.toISOString().slice(0, 16),
        endDate: viewingEvent.end ? viewingEvent.end.toISOString().slice(0, 16) : viewingEvent.start.toISOString().slice(0, 16),
        assigned_to: viewingEvent.assigned_to || [],
        order_number: viewingEvent.order_number || 0,
      });
      setOpenDialog(true);
    }
  };

  const checkEventPermission = (event) => {
    // Admin can edit/delete any event
    if (isAdmin) return true;
    
    // Check if user is assigned to this event
    const assignedTo = event.extendedProps?.assigned_to || [];
    const currentUserId = getCurrentUserId();
    
    return assignedTo.some(staff => 
      String(staff.staff_id) === String(currentUserId)
    );
  };

  const handleContextMenu = (info) => {
    info.jsEvent.preventDefault();
    
    const hasPermission = checkEventPermission(info.event);
    
    if (hasPermission) {
      setAnchorEl(info.jsEvent.currentTarget);
      setContextMenuEvent(info.event);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setContextMenuEvent(null);
  };

  const handleDeleteEvent = async () => {
    if (!contextMenuEvent) return;
    
    try {
      const response = await deleteData('proccess.php', 'calender_V2', contextMenuEvent.id);
      
      if (response.success) {
        // Remove event from calendar
        contextMenuEvent.remove();
        
        // Refresh events list
        const updatedEvents = events.filter(e => String(e.id) !== String(contextMenuEvent.id));
        setEvents(updatedEvents);
        
        showSnackbar('Event deleted successfully', 'success');
      } else {
        showSnackbar('Error deleting event', 'error');
      }
    } catch (err) {
      console.log(err);
      showSnackbar('Error deleting event', 'error');
    }
    
    handleMenuClose();
    setViewEventDialog(false);
  };

  const handleFormSubmit = async () => {
    try {
      // Validate form
      if (!formData.title || !formData.startDate) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      // Ensure assigned_to is an array
      const assignedTo = Array.isArray(formData.assigned_to) ? formData.assigned_to : [];

      // Prepare event data
      const eventData = {
        title: formData.title,
        description: formData.description || '',
        event_type: formData.event_type,
        display_Type: formData.display_Type,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        assigned_to: JSON.stringify(assignedTo),
        order_number: formData.order_number || 0,
        created_by: selectedEvent?.created_by || getCurrentUserId(),
      };

      let response;
      
      if (selectedEvent) {
        // Update existing event
        response = await updateData('proccess.php', 'calender_V2', selectedEvent.id, eventData);
      } else {
        // Create new event
        response = await addData('proccess.php', 'calender_V2', eventData);
      }

      if (response.success) {
        showSnackbar(
          selectedEvent ? 'Event updated successfully' : 'Event created successfully',
          'success'
        );
        
        // Refresh events
        const refreshedEvents = await getData('proccess.php', 'calender_V2');
        if (refreshedEvents.success) {
          const parsedEvents = (Array.isArray(refreshedEvents.data) ? refreshedEvents.data : []).map(event => ({
            ...event,
            assigned_to: parseAssignedTo(event.assigned_to)
          }));
          
          const filteredEvents = filterEventsByUser(parsedEvents);
          setEvents(filteredEvents);
        }
        
        setOpenDialog(false);
        resetForm();
      } else {
        showSnackbar('Error saving event', 'error');
      }
    } catch (err) {
      console.log(err);
      showSnackbar('Error saving event', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_type: 'timed',
      display_Type: isAdmin ? 'public' : 'private',
      startDate: '',
      endDate: '',
      assigned_to: [],
      order_number: 0,
    });
    setSelectedEvent(null);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Format staff name
  const getStaffName = (staff) => {
    if (staff.staff_name) return staff.staff_name;
    
    // Try to find staff in the staffs list
    const foundStaff = staffs.find(s => 
      String(s.Id) === String(staff.staff_id) || String(s.id) === String(staff.staff_id)
    );
    
    if (foundStaff) {
      return `${foundStaff.FirstName || ''} ${foundStaff.LastName || ''}`.trim();
    }
    
    return 'Unknown Staff';
  };

  // Convert events to FullCalendar format
  const calendarEvents = events.map(event => {
    // Check if current user is assigned
    const currentUserId = getCurrentUserId();
    const isAssigned = event.assigned_to?.some(staff => 
      String(staff.staff_id) === String(currentUserId)
    );
    
    return {
      id: event.id,
      title: event.title,
      start: event.startDate,
      end: event.endDate,
      allDay: event.event_type === 'alltime',
      extendedProps: {
        description: event.description,
        event_type: event.event_type,
        display_Type: event.display_Type,
        assigned_to: event.assigned_to,
        order_number: event.order_number,
        created_by: event.created_by,
      },
      // Different colors based on visibility and assignment
      backgroundColor: event.display_Type === 'public' 
        ? colors.greenAccent[500] 
        : isAssigned 
          ? colors.blueAccent[500] 
          : colors.grey[500],
      borderColor: event.display_Type === 'public' 
        ? colors.greenAccent[700] 
        : isAssigned 
          ? colors.blueAccent[700] 
          : colors.grey[700],
      textColor: '#fff',
    };
  });

  // Get assigned staff names for display
  const getAssignedStaffNames = (assignedTo) => {
    if (!assignedTo || assignedTo.length === 0) return 'No staff assigned';
    
    return assignedTo.map(staff => {
      const staffInfo = staffs.find(s => 
        String(s.Id) === String(staff.staff_id) || String(s.id) === String(staff.staff_id)
      );
      
      if (staffInfo) {
        return `${staffInfo.FirstName || ''} ${staffInfo.LastName || ''}`.trim();
      }
      
      return staff.staff_name || 'Unknown';
    }).join(', ');
  };

  return (
    <Box m="20px">
      <HeaderPage title="CALENDAR" subtitle="Full Calendar Interactive Page" />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box display="flex" justifyContent="space-between" height={"80vh"}>
        {/* Events Sidebar */}
        <Box 
          flex="1 1 20%" 
          bgcolor={"transparent"} 
          p="15px" 
          borderRadius="8px" 
          border={`1px solid ${colors.dark[400]}`}
          sx={{ overflowY: 'auto' }}
        >
          <Typography variant="h3" fontWeight={"bold"} color={colors.dark[400]} mb="10px">
            Upcoming Events
          </Typography>
          {currentEvents.length === 0 ? (
            <Typography color={colors.dark[300]}>No upcoming events</Typography>
          ) : (
            <List>
              {currentEvents.slice(0, 10).map(event => (
                <ListItem 
                  key={event.id}
                  sx={{ 
                    bgcolor: colors.dark[500],
                    mb: 1,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    '&:hover': { bgcolor: colors.dark[400] }
                  }}
                  onClick={() => {
                    const calendarApi = document.querySelector('.fc')?._getApi?.();
                    if (calendarApi) {
                      calendarApi.gotoDate(event.start);
                    }
                  }}
                >
                  <ListItemText 
                    primary={event.title}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" display="block">
                          <EventIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                          {event.start?.toLocaleDateString()} 
                          {!event.allDay && ` ${event.start?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                          <Chip 
                            label={event.extendedProps?.display_Type}
                            size="small"
                            sx={{ 
                              bgcolor: event.extendedProps?.display_Type === 'public' 
                                ? colors.greenAccent[500] 
                                : colors.blueAccent[500],
                              color: '#fff',
                              height: '20px',
                              '& .MuiChip-label': { fontSize: '0.7rem' }
                            }}
                          />
                          <Chip 
                            label={`${event.extendedProps?.assigned_to?.length || 0} staff`}
                            size="small"
                            icon={<PersonAddIcon sx={{ fontSize: '0.8rem !important' }} />}
                            sx={{ height: '20px', '& .MuiChip-label': { fontSize: '0.7rem' } }}
                          />
                        </Stack>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Calendar */}
        <Box flex="1 1 80%" ml="20px">
          <FullCalendar
            height="100%"
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
            }}
            editable={isAdmin} // Only admin can drag/drop events
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            events={calendarEvents}
            select={handleDateClick}
            eventClick={handleEventClick}
            eventContextMenu={handleContextMenu}
            eventsSet={(events) => setCurrentEvents(events)}
          />
        </Box>
      </Box>

      {/* Create/Edit Event Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedEvent ? 'Edit Event' : 'Create New Event'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Title"
              fullWidth
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={formData.event_type}
                onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                label="Event Type"
              >
                <MenuItem value="timed">Timed</MenuItem>
                <MenuItem value="alltime">All Day</MenuItem>
              </Select>
            </FormControl>

            {isAdmin && (
              <FormControl fullWidth>
                <InputLabel>Display Type</InputLabel>
                <Select
                  value={formData.display_Type}
                  onChange={(e) => setFormData({ ...formData, display_Type: e.target.value })}
                  label="Display Type"
                >
                  <MenuItem value="public">Public (Visible to all)</MenuItem>
                  <MenuItem value="private">Private (Visible only to assigned staff)</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              label="Start Date"
              type={formData.event_type === 'alltime' ? 'date' : 'datetime-local'}
              fullWidth
              required
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="End Date"
              type={formData.event_type === 'alltime' ? 'date' : 'datetime-local'}
              fullWidth
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth>
              <InputLabel>Assign to Staff</InputLabel>
              <Select
                multiple
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                input={<OutlinedInput label="Assign to Staff" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((staff) => (
                      <Chip 
                        key={staff.staff_id} 
                        label={staff.staff_name || getStaffName(staff)} 
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {staffs.map((staff) => (
                  <MenuItem 
                    key={staff.Id} 
                    value={{ 
                      staff_id: staff.Id, 
                      staff_name: `${staff.FirstName || ''} ${staff.LastName || ''}`.trim()
                    }}
                  >
                    {`${staff.FirstName || ''} ${staff.LastName || ''}`.trim()} 
                    {staff.department && ` (${staff.department})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Order Number"
              type="number"
              fullWidth
              value={formData.order_number}
              onChange={(e) => setFormData({ ...formData, order_number: parseInt(e.target.value) || 0 })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary">
            {selectedEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Event Dialog */}
      <Dialog open={viewEventDialog} onClose={() => setViewEventDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Event Details
          {checkEventPermission({ extendedProps: viewingEvent }) && (
            <IconButton
              aria-label="more"
              sx={{ position: 'absolute', right: 8, top: 8 }}
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setContextMenuEvent({ 
                  id: viewingEvent.id,
                  extendedProps: viewingEvent,
                  remove: () => {} // Placeholder
                });
              }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          {viewingEvent && (
            <Box sx={{ mt: 2 }}>
              <Paper sx={{ p: 2, mb: 2, bgcolor: colors.dark[500] }}>
                <Typography variant="h6" gutterBottom>{viewingEvent.title}</Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {viewingEvent.description || 'No description provided'}
                </Typography>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip 
                    label={viewingEvent.display_Type}
                    size="small"
                    sx={{ 
                      bgcolor: viewingEvent.display_Type === 'public' 
                        ? colors.greenAccent[500] 
                        : colors.blueAccent[500],
                      color: '#fff'
                    }}
                  />
                  <Chip 
                    label={viewingEvent.event_type === 'alltime' ? 'All Day' : 'Timed'}
                    size="small"
                  />
                </Stack>

                <Typography variant="body2" gutterBottom>
                  <strong>Start:</strong> {new Date(viewingEvent.start).toLocaleString()}
                </Typography>
                
                <Typography variant="body2" gutterBottom>
                  <strong>End:</strong> {viewingEvent.end ? new Date(viewingEvent.end).toLocaleString() : 'N/A'}
                </Typography>

                <Typography variant="body2" gutterBottom>
                  <strong>Order Number:</strong> {viewingEvent.order_number || 0}
                </Typography>

                <Typography variant="body2" gutterBottom>
                  <strong>Assigned Staff:</strong>
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {viewingEvent.assigned_to?.length > 0 ? (
                    viewingEvent.assigned_to.map((staff, index) => (
                      <Chip 
                        key={index}
                        label={getStaffName(staff)}
                        size="small"
                        avatar={<Avatar>{getStaffName(staff).charAt(0)}</Avatar>}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No staff assigned
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewEventDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditEvent}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteEvent} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CalendarPage;